import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/apiClient";
import { AdminLayout } from "@/components/AdminLayout";
import { DoctorPhoto } from "@/components/DoctorPhoto";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search, Loader2, ChevronLeft, ChevronRight, Upload, KeyRound, Users,
  ArrowUp, ArrowDown, ArrowUpDown, Plus, Download, ImageDown,
} from "lucide-react";

const EMPTY_MR = { emp_code: "", name: "", division: "", designation: "", hq: "", phone: "", email: "" };

const COLUMNS = [
  { key: "emp_code", label: "Emp Code" },
  { key: "name", label: "Name" },
  { key: "division", label: "Division" },
  { key: "designation", label: "Designation" },
  { key: "hq", label: "HQ" },
  { key: "pledge_count", label: "Pledges Enrolled", right: true },
];

export default function AdminMrsPage() {
  const navigate = useNavigate();
  const [mrData, setMrData] = useState({ items: [], total: 0 });
  const [mrSearch, setMrSearch] = useState("");
  const [mrPage, setMrPage] = useState(1);
  const [mrLoading, setMrLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [sortBy, setSortBy] = useState("emp_code");
  const [sortDir, setSortDir] = useState("asc");
  const [addOpen, setAddOpen] = useState(false);
  const [newMr, setNewMr] = useState(EMPTY_MR);
  const [adding, setAdding] = useState(false);
  const [mrLimit, setMrLimit] = useState(20);

  const setMrField = (k, v) => setNewMr((m) => ({ ...m, [k]: v }));

  const addMr = async () => {
    if (!newMr.emp_code.trim() || newMr.name.trim().length < 2) {
      toast.error("Emp Code and Name are required.");
      return;
    }
    setAdding(true);
    try {
      const res = await api.post("/admin/mrs", newMr);
      toast.success(res.data.message);
      setAddOpen(false);
      setNewMr(EMPTY_MR);
      loadMrs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add MR");
    } finally {
      setAdding(false);
    }
  };

  const loadMrs = useCallback(() => {
    setMrLoading(true);
    api.get("/admin/mrs", { params: { search: mrSearch, page: mrPage, limit: mrLimit, sort_by: sortBy, sort_dir: sortDir } })
      .then((r) => setMrData(r.data))
      .catch(() => {})
      .finally(() => setMrLoading(false));
  }, [mrSearch, mrPage, mrLimit, sortBy, sortDir]);

  useEffect(() => {
    const t = setTimeout(loadMrs, 300);
    return () => clearTimeout(t);
  }, [loadMrs]);

  const sortOn = (key) => {
    setMrPage(1);
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(key); setSortDir(key === "pledge_count" ? "desc" : "asc"); }
  };

  const sortIcon = (key) => {
    if (sortBy !== key) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  };

  const importMrs = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/mrs/import", fd);
      toast.success(`MR import: ${res.data.created} added, ${res.data.updated} updated, ${res.data.skipped} skipped`);
      loadMrs();
    } catch {
      toast.error("Import failed — is it a valid employee-list .xlsx?");
    } finally {
      setImporting(false);
    }
  };

  // Downloads the MR's self-pledge photo named "<NAME>-<EMP ID>.jpg"
  // (filename comes from the backend's Content-Disposition header).
  const downloadMrPhoto = (empCode) => {
    const token = localStorage.getItem("hip_admin_token");
    fetch(`${API}/admin/mrs/${empCode}/photo`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (!res.ok) throw new Error("no photo");
        const filename = (res.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/)?.[1] || `MR-${empCode}.jpg`;
        return res.blob().then((blob) => ({ blob, filename }));
      })
      .then(({ blob, filename }) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Could not download photo"));
  };

  // Excel of the MR list — same search + sort as the table, all pages, no photos.
  const [exporting, setExporting] = useState(false);
  const exportMrs = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/mrs/export", {
        params: { search: mrSearch, sort_by: sortBy, sort_dir: sortDir },
        responseType: "blob",
      });
      const filename = (res.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "healthy-india-mrs.xlsx";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const resetMrPassword = async (empCode) => {
    try {
      const res = await api.post(`/admin/mrs/${empCode}/reset-password`);
      toast.success(res.data.message);
    } catch {
      toast.error("Reset failed");
    }
  };

  const mrTotalPages = Math.max(1, Math.ceil(mrData.total / mrLimit));

  return (
    <AdminLayout>
      <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="mr-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-display font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#0056D2]" /> MR Team ({mrData.total.toLocaleString("en-IN")})
          </h3>
          <div className="flex items-center gap-3">
            <div className="relative min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input data-testid="mr-search" value={mrSearch} onChange={(e) => { setMrPage(1); setMrSearch(e.target.value); }} placeholder="Search emp code, name, HQ…" className="pl-9" />
            </div>
            <label className="inline-flex">
              <input type="file" accept=".xlsx" className="sr-only" onChange={importMrs} data-testid="mr-import-input" />
              <span className={`inline-flex items-center rounded-full border border-slate-300 px-4 h-9 text-sm font-medium cursor-pointer hover:bg-slate-50 ${importing ? "opacity-50 pointer-events-none" : ""}`}>
                {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Import MR list (.xlsx)
              </span>
            </label>
            <Button
              data-testid="mr-export-btn"
              onClick={exportMrs}
              disabled={exporting || mrData.total === 0}
              variant="outline"
              className="rounded-full"
              title="Download the MR list with pledge counts (.xlsx)"
            >
              {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} Export
            </Button>
            <Button data-testid="add-mr-btn" onClick={() => { setNewMr(EMPTY_MR); setAddOpen(true); }} className="rounded-full bg-[#0056D2] hover:bg-blue-700 text-white">
              <Plus className="w-4 h-4 mr-1" /> Add MR
            </Button>
          </div>
        </div>

        <div className="mt-4 overflow-x-auto">
          <Table data-testid="mr-table">
            <TableHeader>
              <TableRow>
                {COLUMNS.map((c) => (
                  <TableHead key={c.key} className={c.right ? "text-right" : undefined}>
                    <button
                      type="button"
                      data-testid={`mr-sort-${c.key}`}
                      onClick={() => sortOn(c.key)}
                      className={`inline-flex items-center gap-1 hover:text-[#0056D2] ${c.right ? "justify-end w-full" : ""} ${sortBy === c.key ? "text-[#0056D2] font-semibold" : ""}`}
                    >
                      {c.label} {sortIcon(c.key)}
                    </button>
                  </TableHead>
                ))}
                <TableHead>Self Pledge</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mrLoading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></TableCell></TableRow>
              ) : mrData.items.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">No MRs found — import the employee list</TableCell></TableRow>
              ) : (
                mrData.items.map((m) => (
                  <TableRow key={m.emp_code}>
                    <TableCell className="font-medium text-slate-900">{m.emp_code}</TableCell>
                    <TableCell>
                      <button
                        type="button"
                        data-testid={`mr-view-${m.emp_code}`}
                        onClick={() => navigate(`/admin/mrs/${m.emp_code}`)}
                        className="text-left font-medium text-[#0056D2] hover:underline"
                        title="View pledges submitted by this MR"
                      >
                        {m.name}
                      </button>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="border-blue-200 bg-blue-50 text-[#0056D2]">{m.division || "—"}</Badge></TableCell>
                    <TableCell className="text-slate-600 text-xs">{m.designation || "—"}</TableCell>
                    <TableCell className="text-slate-600">{m.hq || "—"}</TableCell>
                    <TableCell className="text-right font-display font-bold text-slate-900">{m.pledge_count}</TableCell>
                    <TableCell>
                      {m.self_pledge ? (
                        <span className="flex items-center gap-2" data-testid={`self-pledge-${m.emp_code}`}>
                          <DoctorPhoto pledge={{ name: m.name, photo: m.self_pledge.photo }} />
                          <Button
                            data-testid={`mr-photo-dl-${m.emp_code}`}
                            onClick={() => downloadMrPhoto(m.emp_code)}
                            size="icon" variant="outline"
                            className="rounded-full h-8 w-8 border-slate-200 text-slate-500 hover:text-[#0056D2]"
                            title="Download MR photo"
                          >
                            <ImageDown className="w-3.5 h-3.5" />
                          </Button>
                          <a href={`${API}/certificate/${m.self_pledge.certificate_id}`} title="Download MR certificate">
                            <Button data-testid={`mr-cert-${m.emp_code}`} size="sm" variant="outline" className="rounded-full h-8 border-green-200 text-[#138808] hover:bg-green-50">
                              <Download className="w-3.5 h-3.5 mr-1" /> Certificate
                            </Button>
                          </a>
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">Pending</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        data-testid={`mr-reset-${m.emp_code}`}
                        onClick={() => resetMrPassword(m.emp_code)}
                        size="sm" variant="ghost" className="rounded-full text-slate-500 h-8"
                        title="Reset password to Employee ID"
                      >
                        <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset password
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
          <div className="flex items-center gap-3">
            <span>{mrData.total.toLocaleString("en-IN")} MRs · Page {mrPage} of {mrTotalPages}</span>
            <select
              data-testid="mr-page-size"
              value={mrLimit}
              onChange={(e) => { setMrPage(1); setMrLimit(Number(e.target.value)); }}
              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700"
            >
              {[20, 100, 500].map((n) => <option key={n} value={n}>{n} per page</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <Button data-testid="mr-prev-page" onClick={() => setMrPage((p) => Math.max(1, p - 1))} disabled={mrPage <= 1} size="sm" variant="outline" className="rounded-full"><ChevronLeft className="w-4 h-4" /></Button>
            <Button data-testid="mr-next-page" onClick={() => setMrPage((p) => Math.min(mrTotalPages, p + 1))} disabled={mrPage >= mrTotalPages} size="sm" variant="outline" className="rounded-full"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>

      {/* Manual MR entry */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogTitle className="font-display font-bold text-xl text-slate-900">Add MR</DialogTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="mr-emp" className="text-sm font-medium text-slate-700">Emp Code *</Label>
              <Input data-testid="new-mr-emp" id="mr-emp" value={newMr.emp_code} onChange={(e) => setMrField("emp_code", e.target.value)} className="mt-1.5" placeholder="e.g. 21001" />
            </div>
            <div>
              <Label htmlFor="mr-name" className="text-sm font-medium text-slate-700">Name *</Label>
              <Input data-testid="new-mr-name" id="mr-name" value={newMr.name} onChange={(e) => setMrField("name", e.target.value)} className="mt-1.5" placeholder="Full name" />
            </div>
            <div>
              <Label htmlFor="mr-div" className="text-sm font-medium text-slate-700">Division</Label>
              <Input data-testid="new-mr-division" id="mr-div" value={newMr.division} onChange={(e) => setMrField("division", e.target.value)} className="mt-1.5" placeholder="e.g. BC / BCLS / EXL" />
            </div>
            <div>
              <Label htmlFor="mr-desg" className="text-sm font-medium text-slate-700">Designation</Label>
              <Input data-testid="new-mr-designation" id="mr-desg" value={newMr.designation} onChange={(e) => setMrField("designation", e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="mr-hq" className="text-sm font-medium text-slate-700">HQ</Label>
              <Input data-testid="new-mr-hq" id="mr-hq" value={newMr.hq} onChange={(e) => setMrField("hq", e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="mr-phone" className="text-sm font-medium text-slate-700">Phone</Label>
              <Input data-testid="new-mr-phone" id="mr-phone" value={newMr.phone} onChange={(e) => setMrField("phone", e.target.value)} className="mt-1.5" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="mr-email" className="text-sm font-medium text-slate-700">Email</Label>
              <Input data-testid="new-mr-email" id="mr-email" type="email" value={newMr.email} onChange={(e) => setMrField("email", e.target.value)} className="mt-1.5" />
            </div>
          </div>
          <Button data-testid="save-mr-btn" onClick={addMr} disabled={adding} className="w-full rounded-full bg-[#0056D2] hover:bg-blue-700 text-white font-semibold">
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add MR"}
          </Button>
          <p className="text-xs text-slate-400 text-center -mt-2">First login password will be the Emp Code (same as bulk import).</p>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
